import { useTranslation } from '@/i18n/hook';
import { Link } from 'react-router-dom';
import { guides } from '@/mocks/categories';

const guideSlugs: Record<string, string> = {
  review: 'cssbuy-review-2026',
  shipping: 'cssbuy-shipping-cost',
  warehouse: 'cssbuy-warehouse-guide',
  qc: 'cssbuy-qc-guide',
  coupon: 'cssbuy-coupon-discount',
  discount: 'cssbuy-coupon-discount',
};

const guideImages: Record<string, string> = {
  review: 'https://readdy.ai/api/search-image?query=Customer%20feedback%20five%20star%20rating%20review%20cards%20on%20clean%20cream%20desk%20online%20shopping%20evaluation%20concept%20minimal%20soft%20natural%20light%20warm%20neutral%20tones&width=800&height=500&seq=guide-review&orientation=landscape',
  shipping: 'https://readdy.ai/api/search-image?query=International%20shipping%20cardboard%20boxes%20with%20packing%20tape%20and%20shipping%20labels%20global%20delivery%20concept%20minimal%20clean%20soft%20light%20warm%20tones&width=800&height=500&seq=guide-shipping&orientation=landscape',
  warehouse: 'https://readdy.ai/api/search-image?query=Warehouse%20storage%20shelves%20stacked%20boxes%20organized%20inventory%20logistics%20minimal%20clean%20soft%20light%20warm%20tones&width=800&height=500&seq=guide-warehouse&orientation=landscape',
  qc: 'https://readdy.ai/api/search-image?query=Product%20quality%20inspection%20magnifying%20glass%20over%20clothing%20fabric%20detail%20minimal%20clean%20soft%20light%20warm%20tones&width=800&height=500&seq=guide-qc&orientation=landscape',
  coupon: 'https://readdy.ai/api/search-image?query=Discount%20coupon%20voucher%20ticket%20with%20scissors%20saving%20money%20shopping%20concept%20minimal%20clean%20soft%20light%20warm%20tones&width=800&height=500&seq=guide-coupon&orientation=landscape',
  discount: 'https://readdy.ai/api/search-image?query=Sale%20discount%20percentage%20tag%20label%20on%20product%20retail%20promotion%20concept%20minimal%20clean%20soft%20light%20warm%20tones&width=800&height=500&seq=guide-discount&orientation=landscape',
};

export default function InfoSections() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const steps = [
    { num: '01', title: 'how.step1.title', desc: 'how.step1.desc' },
    { num: '02', title: 'how.step2.title', desc: 'how.step2.desc' },
    { num: '03', title: 'how.step3.title', desc: 'how.step3.desc' },
    { num: '04', title: 'how.step4.title', desc: 'how.step4.desc' },
    { num: '05', title: 'how.step5.title', desc: 'how.step5.desc' },
    { num: '06', title: 'how.step6.title', desc: 'how.step6.desc' },
  ];

  const getGuideTitle = (g: typeof guides[0]) => {
    if (currentLang === 'de') return g.titleDe;
    if (currentLang === 'en') return g.titleEn;
    return g.title;
  };

  return (
    <>
      {/* Spreadsheet Intro */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                {t('spreadsheet.h2')}
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-6">
                {t('spreadsheet.text')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center px-5 py-2.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors text-sm whitespace-nowrap"
              >
                {t('spreadsheet.cta')}
              </a>
            </div>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Organized%20product%20catalog%20grid%20on%20tablet%20screen%20with%20colorful%20fashion%20item%20rows%20modern%20ecommerce%20dashboard%20minimal%20workspace%20soft%20natural%20light&width=800&height=600&seq=spreadsheet-intro&orientation=landscape&nocache=true"
                alt="CSSBuy Spreadsheet interface"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is it */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              {t('what.h2')}
            </h2>
            <p className="text-foreground-700 leading-relaxed">
              {t('what.text')}
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            {t('how.h2')}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step) => (
              <div key={step.num} className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary-100 text-primary-700 font-bold text-sm mb-4">
                  {step.num}
                </span>
                <h3 className="font-semibold text-foreground-800 mb-2">{t(step.title)}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{t(step.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guides */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            {t('guides.h2')}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {guides.map((g) => (
              <Link
                key={g.key}
                to={`/article/${guideSlugs[g.key]}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all flex flex-col"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={guideImages[g.key]}
                    alt={getGuideTitle(g)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4 flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-foreground-800 group-hover:text-primary-700 transition-colors">
                    {getGuideTitle(g)}
                  </span>
                  <i className="ri-arrow-right-line text-primary-600 flex-shrink-0" />
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/article/cssbuy-spreadsheet-guide"
              className="inline-flex items-center px-5 py-2.5 bg-secondary-500 text-background-50 font-medium rounded-lg hover:bg-secondary-600 transition-colors text-sm whitespace-nowrap"
            >
              {t('guides.cta')}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}